import Plugin from '../core/Plugin';

/**
 * CircleAnnotationPlugin - Allows users to circle words and add text annotations above and below
 * Supports parts of speech tagging with prepopulated options
 */
class CircleAnnotationPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'CircleAnnotation';
    this.annotationCounter = 0;
    this.circleButton = null;
    this.selectedRange = null;
    this.contextMenu = null;
    this.contextMenuVisible = false;
    
    // Dropdown hide delay in milliseconds (allows time for clicking dropdown options)
    this.DROPDOWN_HIDE_DELAY = 200;
    
    // Track initialized inputs to prevent duplicate listener attachment
    this.initializedInputs = new WeakSet();
    
    // Store dropdown hide timeouts to prevent race conditions
    this.dropdownHideTimeouts = new WeakMap();

    // Common parts of speech
    this.partsOfSpeech = [
      'noun',
      'verb',
      'adjective',
      'adverb',
      'pronoun',
      'preposition',
      'conjunction',
      'interjection',
      'article',
      'determiner',
    ];
  }

  init() {
    super.init();
  }

  /**
   * Called after the editor is fully initialized
   */
  afterEditorInit() {
    this.setupSelectionListeners();
    this.setupContextMenu();
    this.setupDocumentClickListener();
    this.reinitializeAnnotations();
  }

  /**
   * Reinitialize annotations that were loaded from saved HTML
   * This restores interactivity (contenteditable, event listeners, UI elements)
   */
  reinitializeAnnotations() {
    if (!this.editor.richEditor) return;

    const annotations = this.editor.richEditor.querySelectorAll('.quillkit-circle-annotation');
    
    annotations.forEach((wrapper) => {
      // Skip if already initialized (has delete button)
      if (wrapper.querySelector('.quillkit-annotation-delete')) {
        return;
      }

      // Find the annotation fields
      const annotationAbove = wrapper.querySelector('.quillkit-annotation-above');
      const annotationBelow = wrapper.querySelector('.quillkit-annotation-below');

      // Reinitialize the "above" field if it exists
      if (annotationAbove) {
        const aboveInput = annotationAbove.querySelector('.quillkit-annotation-input');
        if (aboveInput) {
          this.reinitializeAnnotationInput(aboveInput, annotationAbove, 'above');
        }
      }

      // Reinitialize the "below" field if it exists
      if (annotationBelow) {
        const belowInput = annotationBelow.querySelector('.quillkit-annotation-input');
        if (belowInput) {
          this.reinitializeAnnotationInput(belowInput, annotationBelow, 'below');
        }
      }

      // Add delete button
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'quillkit-annotation-delete';
      deleteBtn.innerHTML = '×';
      deleteBtn.title = 'Delete annotation';
      deleteBtn.type = 'button';
      deleteBtn.contentEditable = 'false';
      deleteBtn.setAttribute('data-quillkit-ui', 'true');
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeAnnotation(wrapper);
      });
      
      wrapper.appendChild(deleteBtn);
    });
  }

  /**
   * Reinitialize a single annotation input field
   */
  reinitializeAnnotationInput(input, container, position) {
    // Skip if already initialized (using WeakSet for reliable tracking)
    if (this.initializedInputs.has(input)) {
      return;
    }
    
    // Re-add contentEditable attribute
    input.contentEditable = 'true';
    
    // Re-add placeholder if missing
    if (!input.dataset.placeholder) {
      input.dataset.placeholder = position === 'above' ? 'Part of speech' : 'Notes';
    }

    // Re-attach event listeners
    input.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    input.addEventListener('input', () => {
      this.editor.syncToTextarea();
    });

    // For "above" field, re-create dropdown
    if (position === 'above') {
      // Check if dropdown already exists
      let dropdown = container.querySelector('.quillkit-annotation-dropdown');
      
      if (!dropdown) {
        dropdown = document.createElement('span');
        dropdown.className = 'quillkit-annotation-dropdown';
        dropdown.style.display = 'none';
        dropdown.contentEditable = 'false';
        dropdown.setAttribute('data-quillkit-ui', 'true');

        // Add parts of speech options
        this.partsOfSpeech.forEach((pos) => {
          const option = document.createElement('span');
          option.className = 'quillkit-annotation-option';
          option.textContent = pos;
          option.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            input.textContent = pos;
            dropdown.style.display = 'none';
            this.editor.syncToTextarea();
          });
          dropdown.appendChild(option);
        });

        container.appendChild(dropdown);
      }

      // Attach dropdown show/hide listeners
      input.addEventListener('focus', (e) => {
        e.stopPropagation();
        // Clear any pending hide timeout to prevent race conditions
        const existingTimeout = this.dropdownHideTimeouts.get(input);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          this.dropdownHideTimeouts.delete(input);
        }
        dropdown.style.display = 'block';
      });

      // Dropdown hide handler (separate from history save)
      input.addEventListener('blur', () => {
        // Use timeout to allow clicking dropdown options
        const timeoutId = setTimeout(() => {
          dropdown.style.display = 'none';
          this.dropdownHideTimeouts.delete(input);
        }, this.DROPDOWN_HIDE_DELAY);
        this.dropdownHideTimeouts.set(input, timeoutId);
      });
    }
    
    // Save to history on blur (for both above and below fields)
    input.addEventListener('blur', () => {
      this.editor.saveToHistory();
    });
    
    // Mark as initialized to prevent duplicate listener attachment
    this.initializedInputs.add(input);
  }

  destroy() {
    if (this.mouseupHandler) {
      this.editor.richEditor?.removeEventListener('mouseup', this.mouseupHandler);
    }
    if (this.dblclickHandler) {
      this.editor.richEditor?.removeEventListener('dblclick', this.dblclickHandler);
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    this.removeCircleButton();
    this.destroyContextMenu();
    super.destroy();
  }

  getToolbarButtons() {
    return [
      {
        name: 'circle-annotation',
        buttons: [
          { command: 'circleWord', label: '⭕', title: 'Circle Word (Click on word in editor)' },
          { command: 'removeCircle', label: '⭕✕', title: 'Remove Circle' },
        ],
      },
    ];
  }

  executeCommand(command) {
    if (command === 'circleWord') {
      this.circleSelectedText();
    } else if (command === 'removeCircle') {
      this.removeCircleAtCursor();
    }
  }

  /**
   * Circle the currently selected text when toolbar button is clicked
   */
  circleSelectedText() {
    if (this.editor.mode !== 'rich') {
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      this.showNoSelectionAlert();
      return;
    }

    const range = selection.getRangeAt(0);
    const text = range.toString().trim();

    if (text.length === 0) {
      this.showNoSelectionAlert();
      return;
    }

    // Circle the selected text
    this.circleWord(range);
  }

  /**
   * Show alert when user tries to circle without selecting text
   */
  showNoSelectionAlert() {
    alert('Please select some text to circle first.');
  }

  /**
   * Setup selection listeners for double-click and drag selection
   * Note: Context menu (right-click) is the primary workflow.
   * Double-click/drag also work for convenience.
   */
  setupSelectionListeners() {
    if (!this.editor.richEditor) return;

    // Handle double-click to select word
    this.dblclickHandler = (event) => {
      if (this.editor.mode !== 'rich' || !this.enabled) return;
      // Don't interfere with clicks on existing annotations or toolbar
      if (
        event.target.closest('.quillkit-circle-annotation') ||
        event.target.closest('.quillkit-editor-toolbar')
      ) {
        return;
      }
      setTimeout(() => {
        let selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && selection.isCollapsed) {
          // No selection: select the word under cursor
          const range = document.caretRangeFromPoint
            ? document.caretRangeFromPoint(event.clientX, event.clientY)
            : document.caretPositionFromPoint
              ? (() => {
                const pos = document.caretPositionFromPoint(event.clientX, event.clientY);
                if (!pos) return null;
                const r = document.createRange();
                r.setStart(pos.offsetNode, pos.offset);
                r.setEnd(pos.offsetNode, pos.offset);
                return r;
              })()
              : null;
          if (range) {
            const wordRange = this.expandRangeToWord(range);
            if (wordRange) {
              selection.removeAllRanges();
              selection.addRange(wordRange);
            }
          }
        }
        selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
          this.showCircleButton(selection.getRangeAt(0));
        }
      }, 10);
    };

    // Helper: expand a collapsed range to the word under the caret
    this.expandRangeToWord = function(range) {
      if (!range || !range.startContainer || range.startContainer.nodeType !== Node.TEXT_NODE) return null;
      const text = range.startContainer.textContent;
      let start = range.startOffset, end = range.startOffset;
      while (start > 0 && /\w/.test(text[start - 1])) start--;
      while (end < text.length && /\w/.test(text[end])) end++;
      if (start === end) return null;
      const wordRange = document.createRange();
      wordRange.setStart(range.startContainer, start);
      wordRange.setEnd(range.startContainer, end);
      return wordRange;
    };


    // Handle mouseup for drag selection
    this.mouseupHandler = (event) => {
      if (this.editor.mode !== 'rich' || !this.enabled) return;

      // Don't interfere with clicks on existing annotations, toolbar, or circle button
      if (
        event.target.closest('.quillkit-circle-annotation') ||
        event.target.closest('.quillkit-editor-toolbar') ||
        event.target.closest('.quillkit-circle-button')
      ) {
        return;
      }

      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
          const range = selection.getRangeAt(0);
          const text = range.toString().trim();
          if (text.length > 0) {
            this.showCircleButton(range);
          }
        } else {
          this.removeCircleButton();
        }
      }, 10);
    };

    this.editor.richEditor.addEventListener('dblclick', this.dblclickHandler);
    this.editor.richEditor.addEventListener('mouseup', this.mouseupHandler);
  }

  /**
   * Setup document-wide click listener to handle click-away behavior
   */
  setupDocumentClickListener() {
    this.documentClickHandler = (event) => {
      // Check if click is outside any annotation
      const clickedAnnotation = event.target.closest('.quillkit-circle-annotation');

      if (!clickedAnnotation) {
        // Clean up empty annotations
        this.cleanupEmptyAnnotations();
      }

      // Hide context menu if clicking outside (null-safe check)
      if (this.contextMenuVisible && this.contextMenu && !this.contextMenu.contains(event.target)) {
        this.hideContextMenu();
      }
    };

    document.addEventListener('click', this.documentClickHandler);
  }

  /**
   * Setup context menu for right-click annotation creation
   */
  setupContextMenu() {
    if (!this.editor.richEditor) return;

    // Create context menu element
    this.contextMenu = document.createElement('div');
    this.contextMenu.className = 'quillkit-context-menu';
    document.body.appendChild(this.contextMenu);

    // Add contextmenu event listener to editor
    this.contextMenuHandler = (e) => {
      if (this.editor.mode !== 'rich' || !this.enabled) return;

      // Don't interfere with clicks on existing annotations
      if (e.target.closest('.quillkit-circle-annotation')) {
        return;
      }

      // Check if there's selected text
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        const text = range.toString().trim();
        if (text.length > 0) {
          e.preventDefault();
          this.selectedRange = range.cloneRange();
          this.showContextMenu(e);
          return;
        }
      }

      // No valid selection, hide menu
      this.hideContextMenu();
    };

    this.editor.richEditor.addEventListener('contextmenu', this.contextMenuHandler);

    // Hide context menu on escape key
    this.contextMenuKeyHandler = (e) => {
      if (e.key === 'Escape' && this.contextMenuVisible) {
        this.hideContextMenu();
      }
    };
    document.addEventListener('keydown', this.contextMenuKeyHandler);

    // Hide context menu on scroll
    this.contextMenuScrollHandler = () => {
      if (this.contextMenuVisible) {
        this.hideContextMenu();
      }
    };
    this.editor.richEditor.addEventListener('scroll', this.contextMenuScrollHandler);
  }

  /**
   * Destroy context menu and cleanup event listeners
   */
  destroyContextMenu() {
    if (this.contextMenu && this.contextMenu.parentNode) {
      this.contextMenu.parentNode.removeChild(this.contextMenu);
    }
    this.contextMenu = null;

    if (this.contextMenuHandler) {
      this.editor.richEditor?.removeEventListener('contextmenu', this.contextMenuHandler);
    }
    if (this.contextMenuKeyHandler) {
      document.removeEventListener('keydown', this.contextMenuKeyHandler);
    }
    if (this.contextMenuScrollHandler) {
      this.editor.richEditor?.removeEventListener('scroll', this.contextMenuScrollHandler);
    }
  }

  /**
   * Get context menu items for annotation
   */
  getContextMenuItems() {
    return [
      {
        label: 'Add Annotation',
        icon: '⭕',
        command: 'addAnnotation',
      },
    ];
  }

  /**
   * Show context menu at mouse position
   */
  showContextMenu(e) {
    // Clear existing menu items
    this.contextMenu.innerHTML = '';

    // Get menu items
    const items = this.getContextMenuItems();

    // Create menu items
    items.forEach((item) => {
      const menuItem = document.createElement('button');
      menuItem.className = 'quillkit-context-menu-item';

      const icon = document.createElement('span');
      icon.className = 'quillkit-context-menu-icon';
      icon.textContent = item.icon;

      const label = document.createElement('span');
      label.className = 'quillkit-context-menu-label';
      label.textContent = item.label;

      menuItem.appendChild(icon);
      menuItem.appendChild(label);

      menuItem.addEventListener('click', () => {
        if (item.command === 'addAnnotation') {
          if (this.selectedRange) {
            this.circleWord(this.selectedRange);
            this.selectedRange = null;
          }
        }
        this.hideContextMenu();
      });

      this.contextMenu.appendChild(menuItem);
    });

    // Position the menu at mouse position
    this.contextMenu.style.left = `${e.pageX}px`;
    this.contextMenu.style.top = `${e.pageY}px`;
    this.contextMenu.classList.add('show');
    this.contextMenuVisible = true;

    // Adjust position if menu goes off-screen
    setTimeout(() => {
      // Check if context menu still exists (might have been destroyed)
      if (!this.contextMenu) return;
      
      const rect = this.contextMenu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (rect.right > viewportWidth) {
        this.contextMenu.style.left = `${e.pageX - rect.width}px`;
      }
      if (rect.bottom > viewportHeight) {
        this.contextMenu.style.top = `${e.pageY - rect.height}px`;
      }
    }, 0);
  }

  /**
   * Hide context menu
   */
  hideContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.classList.remove('show');
      this.contextMenuVisible = false;
    }
  }

  /**
   * Show circle button above selected text
   */
  showCircleButton(range) {
    this.selectedRange = range.cloneRange();

    // Remove existing button if any
    this.removeCircleButton();

    // Create circle button
    this.circleButton = document.createElement('button');
    this.circleButton.className = 'quillkit-circle-button';
    this.circleButton.innerHTML = '⭕';
    this.circleButton.title = 'Add annotation';
    this.circleButton.type = 'button';

    // Position button above selection
    const rect = range.getBoundingClientRect();
    const editorRect = this.editor.richEditor.getBoundingClientRect();

    this.circleButton.style.position = 'absolute';
    this.circleButton.style.left = (rect.left + rect.width / 2 - editorRect.left + this.editor.richEditor.scrollLeft) + 'px';
    this.circleButton.style.top = (rect.top - editorRect.top + this.editor.richEditor.scrollTop - 35) + 'px';

    // Add click handler
    this.circleButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.selectedRange) {
        this.circleWord(this.selectedRange);
        this.removeCircleButton();
      }
    });

    // Add to editor
    this.editor.richEditor.appendChild(this.circleButton);
  }

  /**
   * Remove circle button
   */
  removeCircleButton() {
    if (this.circleButton && this.circleButton.parentNode) {
      this.circleButton.parentNode.removeChild(this.circleButton);
    }
    this.circleButton = null;
    this.selectedRange = null;
  }

  /**
   * Clean up empty annotations when clicking away
   */
  cleanupEmptyAnnotations() {
    const annotations = this.editor.richEditor.querySelectorAll('.quillkit-circle-annotation');
    annotations.forEach((wrapper) => {
      const annotationAbove = wrapper.querySelector('.quillkit-annotation-above');
      const annotationBelow = wrapper.querySelector('.quillkit-annotation-below');
      const aboveInput = annotationAbove?.querySelector('.quillkit-annotation-input');
      const belowInput = annotationBelow?.querySelector('.quillkit-annotation-input');
      const aboveEmpty = !aboveInput || aboveInput.textContent.trim() === '';
      const belowEmpty = !belowInput || belowInput.textContent.trim() === '';
      let changed = false;
      // Remove empty annotation lines individually
      if (aboveEmpty && annotationAbove) {
        annotationAbove.remove();
        changed = true;
      }
      if (belowEmpty && annotationBelow) {
        annotationBelow.remove();
        changed = true;
      }
      // If both are now gone, remove the annotation group and padding
      if ((!annotationAbove || aboveEmpty) && (!annotationBelow || belowEmpty)) {
        const circledWord = wrapper.querySelector('.quillkit-circled-word');
        const wordText = circledWord ? circledWord.textContent : '';
        const textNode = document.createTextNode(wordText);
        wrapper.parentNode.replaceChild(textNode, wrapper);
        this.removePaddingFromLine(textNode);
        changed = true;
      } else if (changed) {
        // If only one was removed, check if both annotation lines are now gone and remove padding if so
        if (!wrapper.querySelector('.quillkit-annotation-above') && !wrapper.querySelector('.quillkit-annotation-below')) {
          this.removePaddingFromLine(wrapper);
        }
      }
      if (changed) {
        this.editor.syncToTextarea();
        this.editor.saveToHistory();
      }
    });
  }

  /**
   * Circle a word at the given range with parts of speech support
   */
  circleWord(range) {
    const wordText = range.toString();
    // Leave fields empty - user will fill them in via dropdown or custom text
    const prefillAbove = ''; // Empty - user selects from dropdown or types custom
    const prefillBelow = ''; // Empty - user types notes
    // Create the circled word wrapper
    const wrapper = document.createElement('span');
    wrapper.className = 'quillkit-circle-annotation';
    wrapper.dataset.annotationId = `annotation-${++this.annotationCounter}`;
    // Create the circled word element (oval shape)
    const circledWord = document.createElement('span');
    circledWord.className = 'quillkit-circled-word';
    circledWord.textContent = wordText;
    // Create annotation above with dropdown
    const annotationAbove = this.createAnnotationField('above', prefillAbove);
    // Create annotation below with dropdown
    const annotationBelow = this.createAnnotationField('below', prefillBelow);
    // Create delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'quillkit-annotation-delete';
    deleteBtn.innerHTML = '×';
    deleteBtn.title = 'Delete annotation';
    deleteBtn.type = 'button';
    deleteBtn.contentEditable = 'false';
    deleteBtn.setAttribute('data-quillkit-ui', 'true'); // Mark as UI-only element to exclude from HTML export
    deleteBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.removeAnnotation(wrapper);
    });

    // Assemble the structure
    wrapper.appendChild(annotationAbove);
    wrapper.appendChild(circledWord);
    wrapper.appendChild(annotationBelow);
    wrapper.appendChild(deleteBtn);

    // Add padding to the line to prevent overlap
    this.addPaddingToLine(range);

    // Replace the selected text with the wrapper
    range.deleteContents();
    range.insertNode(wrapper);

    // Move cursor after the annotation
    const newRange = document.createRange();
    newRange.setStartAfter(wrapper);
    newRange.setEndAfter(wrapper);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(newRange);

    this.editor.syncToTextarea();
    this.editor.saveToHistory();
  }

  /**
   * Create an annotation field with parts of speech dropdown
   */
  createAnnotationField(position, prefill) {
    const container = document.createElement('span');
    container.className = `quillkit-annotation-${position}`;

    const input = document.createElement('span');
    input.className = 'quillkit-annotation-input';
    input.contentEditable = 'true';
    input.dataset.placeholder = position === 'above' ? 'Part of speech' : 'Notes';
    if (prefill) input.textContent = prefill;

    const dropdown = document.createElement('span');
    dropdown.className = 'quillkit-annotation-dropdown';
    dropdown.style.display = 'none';
    dropdown.contentEditable = 'false'; // Prevent user from editing dropdown content
    dropdown.setAttribute('data-quillkit-ui', 'true'); // Mark as UI-only element to exclude from HTML export

    // Add parts of speech options
    this.partsOfSpeech.forEach((pos) => {
      const option = document.createElement('span');
      option.className = 'quillkit-annotation-option';
      option.textContent = pos;
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        input.textContent = pos;
        dropdown.style.display = 'none';
        this.editor.syncToTextarea();
      });
      dropdown.appendChild(option);
    });

    // Show dropdown on focus (only for the above field - parts of speech)
    if (position === 'above') {
      input.addEventListener('focus', (e) => {
        e.stopPropagation();
        // Clear any pending hide timeout to prevent race conditions
        const existingTimeout = this.dropdownHideTimeouts.get(input);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          this.dropdownHideTimeouts.delete(input);
        }
        dropdown.style.display = 'block';
      });

      input.addEventListener('blur', () => {
        // Use timeout to allow clicking dropdown options
        const timeoutId = setTimeout(() => {
          dropdown.style.display = 'none';
          this.dropdownHideTimeouts.delete(input);
        }, this.DROPDOWN_HIDE_DELAY);
        this.dropdownHideTimeouts.set(input, timeoutId);
      });
    }

    input.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    input.addEventListener('input', () => {
      this.editor.syncToTextarea();
    });

    // Save to history only on blur, not on every keystroke
    input.addEventListener('blur', () => {
      this.editor.saveToHistory();
    });

    container.appendChild(input);
    if (position === 'above') {
      container.appendChild(dropdown);
    }

    return container;
  }

  /**
   * Add padding to the line containing the annotation to prevent overlap
   */
  addPaddingToLine(range) {
    let lineElement = range.startContainer;

    // Find the block-level parent element
    while (lineElement && lineElement !== this.editor.richEditor) {
      if (lineElement.nodeType === Node.ELEMENT_NODE) {
        const display = window.getComputedStyle(lineElement).display;
        if (display === 'block' || lineElement.tagName === 'P' || lineElement.tagName === 'DIV') {
          break;
        }
      }
      lineElement = lineElement.parentNode;
    }

    if (lineElement && lineElement !== this.editor.richEditor) {
      // Add padding top and bottom
      const currentPaddingTop = parseInt(lineElement.style.paddingTop || '0', 10);
      const currentPaddingBottom = parseInt(lineElement.style.paddingBottom || '0', 10);

      lineElement.style.paddingTop = Math.max(currentPaddingTop, 30) + 'px';
      lineElement.style.paddingBottom = Math.max(currentPaddingBottom, 30) + 'px';
      lineElement.dataset.hasAnnotationPadding = 'true';
    }
  }

  /**
   * Remove padding from the line when annotation is deleted
   */
  removePaddingFromLine(textNode) {
    let lineElement = textNode.parentNode;

    // Find the block-level parent element
    while (lineElement && lineElement !== this.editor.richEditor) {
      if (lineElement.nodeType === Node.ELEMENT_NODE) {
        const display = window.getComputedStyle(lineElement).display;
        if (display === 'block' || lineElement.tagName === 'P' || lineElement.tagName === 'DIV') {
          break;
        }
      }
      lineElement = lineElement.parentNode;
    }

    if (lineElement && lineElement.dataset.hasAnnotationPadding) {
      // Check if there are any other annotations in this line
      const annotations = lineElement.querySelectorAll('.quillkit-circle-annotation');

      if (annotations.length === 0) {
        // No more annotations, remove padding
        lineElement.style.paddingTop = '';
        lineElement.style.paddingBottom = '';
        delete lineElement.dataset.hasAnnotationPadding;
      }
    }
  }

  /**
   * Remove an annotation wrapper
   */
  removeAnnotation(wrapper) {
    const wordText = wrapper.querySelector('.quillkit-circled-word')?.textContent || '';

    // Replace the annotation with just the text
    const textNode = document.createTextNode(wordText);
    wrapper.parentNode.replaceChild(textNode, wrapper);

    // Remove padding if needed
    this.removePaddingFromLine(textNode);

    this.editor.syncToTextarea();
    this.editor.saveToHistory();
  }

  /**
   * Remove circle annotation at cursor position
   */
  removeCircleAtCursor() {
    if (this.editor.mode !== 'rich') {
      return;
    }

    const selection = window.getSelection();
    if (!selection.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    let node = range.startContainer;

    // Find the closest circle annotation element
    while (node && node !== this.editor.richEditor) {
      if (node.classList && node.classList.contains('quillkit-circle-annotation')) {
        this.removeAnnotation(node);
        return;
      }
      node = node.parentNode;
    }

    // No annotation found - silently ignore
  }

  getMetadata() {
    return {
      name: 'Circle Annotation',
      version: '1.0.0',
      description: 'Circle words and add text annotations above and below',
      author: 'AtlaraFirm',
    };
  }
}

export default CircleAnnotationPlugin;
